import { useState, useRef, type ChangeEvent } from 'react';

import { useDocuments } from '../hooks/use-documents';
import {
  FileTooLargeError,
  UnsupportedMimeError,
  useUploadDocument,
} from '../hooks/use-upload-document';

import type { DocumentSummary } from '../../../lib/types';

interface DocumentPickerProps {
  open: boolean;
  bondId: string | undefined;
  onClose: () => void;
  /**
   * Item 236 (29/Mai/2026) — passa o DocumentSummary completo (não apenas
   * id+name) para que o Composer possa montar o card otimista no bubble
   * outbound (precisa de mimeType + sizeBytes além do id+name).
   */
  onConfirm: (document: DocumentSummary) => void;
}

/**
 * DocumentPicker — Item 36 PR-B2.
 *
 * Modal de seleção de Document para anexar à mensagem. Lista unificada (D4):
 * - Botão "Subir novo arquivo" no topo (input file hidden via label)
 * - Input de busca por nome (filtra via useDocuments(bondId, search))
 * - Lista de Documents (ícone por mime + nome + tamanho formatado)
 * - Estado vazio educativo
 * - Loading + error states
 * - Selection de 1 doc por vez (radio-like behavior via click)
 * - Footer: Cancelar + Anexar (N)
 * - Modal backdrop com z-index alto
 *
 * Upload faz POST multipart com validação 25MB + MIME whitelist (D6).
 */
export function DocumentPicker({
  open,
  bondId,
  onClose,
  onConfirm,
}: DocumentPickerProps): JSX.Element | null {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Item 285 (29/Mai/2026) — preservar doc uploaded em conversa sem bond.
  // Quando bondId=undefined, useDocuments retorna [] (query disabled) e o
  // upload novo NÃO aparece na list. Sem este state, documents.find(selectedId)
  // retorna undefined e o nome do anexo se perde no onConfirm.
  const [lastUploaded, setLastUploaded] = useState<DocumentSummary | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { documents, isLoading, error } = useDocuments(bondId, search);
  const { upload, isUploading } = useUploadDocument(bondId);

  if (!open) return null;

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    setUploadError(null);
    try {
      const doc = await upload(file);
      setSelectedId(doc.id);
      setLastUploaded(doc);
    } catch (err) {
      if (err instanceof FileTooLargeError) {
        setUploadError(
          `Arquivo muito grande: ${(file.size / 1024 / 1024).toFixed(1)}MB (máx 25MB).`,
        );
      } else if (err instanceof UnsupportedMimeError) {
        setUploadError(
          `Tipo ${file.type} não suportado. Aceitos: PDF, JPEG, PNG, MP4, MP3, M4A, DOCX, XLSX.`,
        );
      } else {
        setUploadError('Falha no upload — tente novamente.');
      }
    }
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }
  };

  const onConfirmClick = (): void => {
    if (selectedId === null) return;
    // Item 285: fallback ao lastUploaded para conversa sem bond (useDocuments
    // disabled → list vazia → find não acha o doc recém-uploaded).
    const selectedDoc =
      documents.find((d) => d.id === selectedId) ??
      (lastUploaded?.id === selectedId ? lastUploaded : null);
    if (selectedDoc === null) return; // sem doc selecionável, mantém modal aberto
    onConfirm(selectedDoc);
    setSelectedId(null);
    setSearch('');
    setLastUploaded(null);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-label="Anexar documento"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--bg-1, white)',
          borderRadius: 8,
          maxWidth: 480,
          width: '90%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid var(--border-subtle, #eee)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <strong>Anexar documento</strong>
          <button type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>

        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label
            className="btn btn--secondary btn--sm"
            style={{ cursor: 'pointer', textAlign: 'center' }}
          >
            + Subir novo arquivo
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.mp4,.mp3,.m4a,.docx,.xlsx,application/pdf,image/jpeg,image/png,video/mp4,audio/mpeg,audio/mp4,audio/ogg,audio/wav,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => void onFileChange(e)}
              disabled={isUploading}
              style={{ display: 'none' }}
              aria-label="Subir novo arquivo"
            />
          </label>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: 8,
              border: '1px solid var(--border-subtle, #ddd)',
              borderRadius: 4,
              fontSize: 14,
            }}
          />
          {uploadError !== null && (
            <div style={{ color: 'var(--danger-fg, red)', fontSize: 12 }}>{uploadError}</div>
          )}
          {isUploading && <div style={{ fontSize: 12, opacity: 0.7 }}>Enviando…</div>}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 12px' }}>
          {isLoading && <div>Carregando documentos…</div>}
          {error !== null && (
            <div style={{ color: 'var(--danger-fg, red)' }}>Erro ao carregar documentos.</div>
          )}
          {!isLoading && documents.length === 0 && lastUploaded === null && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3, #888)' }}>
              {bondId === undefined ? (
                <>
                  Conversa sem vínculo (bond). Você pode subir um arquivo novo acima — ele será
                  anexado a esta mensagem, mas não aparecerá em listagens prévias.
                </>
              ) : (
                <>
                  Nenhum documento vinculado a este vínculo ainda. Use &quot;Subir novo
                  arquivo&quot; acima.
                </>
              )}
            </div>
          )}
          {/* Item 285 — render do doc recém-uploaded quando bondId undefined
              (list não tem porque useDocuments está disabled). Permite usuário
              visualizar/selecionar/desselecionar antes de confirmar. */}
          {lastUploaded !== null &&
            documents.find((d) => d.id === lastUploaded.id) === undefined && (
              <DocumentRow
                key={lastUploaded.id}
                doc={lastUploaded}
                selected={selectedId === lastUploaded.id}
                onClick={() => setSelectedId(lastUploaded.id)}
              />
            )}
          {documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              selected={selectedId === doc.id}
              onClick={() => setSelectedId(doc.id)}
            />
          ))}
        </div>

        <div
          style={{
            padding: 12,
            borderTop: '1px solid var(--border-subtle, #eee)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={onConfirmClick}
            disabled={selectedId === null}
          >
            {selectedId !== null ? 'Anexar (1)' : 'Anexar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentRow({
  doc,
  selected,
  onClick,
}: {
  doc: DocumentSummary;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 8,
        width: '100%',
        background: selected ? 'var(--brand-subtle)' : 'transparent',
        border: '1px solid var(--border-subtle, #eee)',
        borderRadius: 4,
        marginBottom: 4,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <DocIcon mime={doc.mimeType} />
      <div style={{ flex: 1 }}>
        <div>{doc.name}</div>
        <div style={{ fontSize: 11, opacity: 0.6 }}>{formatBytes(doc.sizeBytes)}</div>
      </div>
    </button>
  );
}

function DocIcon({ mime }: { mime: string }): JSX.Element {
  if (mime === 'application/pdf') return <span aria-hidden>📄</span>;
  if (mime.startsWith('image/')) return <span aria-hidden>🖼</span>;
  if (mime.startsWith('video/')) return <span aria-hidden>🎬</span>;
  if (mime.startsWith('audio/')) return <span aria-hidden>🎵</span>;
  return <span aria-hidden>📎</span>;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
